import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { mockPriceCart } from '@/lib/api-mock';
import { useCart } from '@/lib/cart-store';
/**
 * Single source of truth for cart line pricing + availability.
 *
 * Driven by the live cart contents (productProfileId + quantity). The query
 * key includes the items signature so any mutation re-fetches. SSE-driven
 * stock changes invalidate `['cart-pricing']` (see catalog-events.ts) so the
 * cart UI re-validates inventory without a refresh.
 *
 * Consumed by CartPage, SideCartPanel, MobileCartBar - every visible cart
 * surface reads from the same `availabilityError` data, so client + server
 * stay in lockstep.
 */
export const CART_PRICING_QUERY_KEY = ['cart-pricing'];
export function useCartPricing() {
    const { items } = useCart();
    const itemsKey = useMemo(() => items.map((i) => `${i.productProfileId}:${i.quantity}`).join('|'), [items]);
    const { data, isLoading, refetch } = useQuery({
        queryKey: [...CART_PRICING_QUERY_KEY, itemsKey],
        queryFn: () => mockPriceCart({
            cart: { items: items },
            fulfillment: { type: 'pickup' },
        }),
        enabled: items.length > 0,
        // Always treat as stale so SSE invalidation triggers a real re-fetch.
        staleTime: 0,
        gcTime: 60_000,
        // Keep previous data visible while re-fetching to avoid layout flashes.
        placeholderData: (prev) => prev,
    });
    return {
        data: data ?? null,
        isLoading,
        refetch: () => {
            void refetch();
        },
    };
}
